import { Flex, type FlexProps, Heading, Text } from "@chakra-ui/react";
import { type Address, type ChainFullId, ChainFullIds, formatBalance } from "@umami/chains";
import { useAddressKind } from "@umami/components";
import { useGetNetworkSymbol } from "@umami/state";
import { useWatch } from "react-hook-form";

import { AddressTileIcon } from "./AddressTileIcon";
import { useColor } from "../../styles/useColor";
import { AccountTileWrapper } from "../AccountTile";
import { CopyAddressButton } from "../CopyAddressButton";

/**
 * Tile component for displaying account (owned / unknown) / contact / baker.
 *
 * Contains icon, label (optional), address and balance (optional).
 * Displays tooltip with the full label when hovered (if label is present).
 *
 * @param address - Account address.
 * @param balance - Balance of the account.
 * @param flexProps - Defines component style.
 * @param hideBalance - If true, balance will not be displayed.
 */
export const AddressTile = ({
  size = "sm",
  address,
  balance,
  hideBalance = false,
  ...flexProps
}: {
  address: Address;
  balance?: string;
  size?: "xs" | "sm";
  hideBalance?: boolean;
} & FlexProps) => {
  const color = useColor();
  const chainId = useWatch<{ chainId?: ChainFullId }>({ name: "chainId" }) ?? ChainFullIds.TEZOS;
  const addressKind = useAddressKind(address, chainId);
  const symbol = useGetNetworkSymbol()(chainId);

  const isSmall = size === "xs";
  const isUnknown = addressKind.type === "unknown";

  return (
    <Flex
      alignItems="center"
      justifyContent="space-between"
      width="100%"
      padding={isUnknown ? "12px 0 12px 16px" : "12px 16px 12px 16px"}
      background={color("100")}
      borderRadius="4px"
      data-testid="address-tile"
      {...flexProps}
    >
      <Flex alignItems="center" gap="16px" width="full">
        <AccountTileWrapper size={size}>
          <AddressTileIcon addressKind={addressKind} size={size} />
        </AccountTileWrapper>

        <Flex
          alignItems={isSmall ? "center" : "flex-start"}
          flexDirection={isSmall ? "row" : "column"}
          width="100%"
          minWidth="0"
        >
          {addressKind.label && (
            <Flex
              alignItems="center"
              overflow="hidden"
              width="100%"
              marginRight="10px"
              whiteSpace="nowrap"
            >
              <Heading overflow="hidden" textOverflow="ellipsis" size={isSmall ? "sm" : "md"}>
                {addressKind.label}
              </Heading>
            </Flex>
          )}

          <Flex alignItems="center" justifyContent="space-between" flexDirection="row" width="100%">
            <CopyAddressButton
              color={color("700")}
              fontSize={isSmall ? "12px" : "14px"}
              address={address.pkh}
              isCopyDisabled={isSmall}
              isLong={isUnknown}
              variant={isSmall ? "unstyled" : "ghost"}
            />
            {!hideBalance && balance !== undefined && (
              <Text size="sm">
                {formatBalance(balance)} {symbol}
              </Text>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};
